import '@code-dot-org/component-library-styles/colors.css';
import '@code-dot-org/component-library-styles/brandOverrides.css';
import '@code-dot-org/component-library-styles/fontVariables.css';
import '@code-dot-org/component-library-styles/shapeAndSpacingVariables.css';
import '@code-dot-org/component-library-styles/primitiveColors.css';
import {ThemeProvider as MuiThemeProvider} from '@mui/material/styles';
import React, {ReactElement} from 'react';
import ReactDOM from 'react-dom';
import {createRoot, Root} from 'react-dom/client';

import {getCurrentBrand, getMuiThemeForBrand} from './brand';
import {SiteConfigProvider} from './SiteConfigContext';

interface Options {
  legacyReactDomRender?: boolean;
}

const rootsByContainer = new WeakMap<Element, Root>();

/**
 * Global bootstrapper function that wraps rendered DOM trees with configured providers
 *
 * This function allows each webpack endpoint to consistently apply global wrappers
 * around their rendered components.
 *
 * @param component - The React component to render
 * @param container - The container element or selector to render into
 * @param options - Option to override default to use legacy rendering behavior
 */
export function createReactRoot(
  component: ReactElement,
  container: Element | string,
  options?: Options
): void {
  const containerElement =
    typeof container === 'string'
      ? document.querySelector(container)
      : container;

  if (!containerElement) {
    throw new Error(
      `createReactRoot: Could not find container element: ${container}`
    );
  }

  const brand = getCurrentBrand();
  const theme = getMuiThemeForBrand(brand);
  const wrappedComponent = (
    <SiteConfigProvider config={{brand}}>
      <MuiThemeProvider theme={theme}>{component}</MuiThemeProvider>
    </SiteConfigProvider>
  );

  if (options?.legacyReactDomRender) {
    ReactDOM.render(wrappedComponent, containerElement);
    return;
  }

  let root = rootsByContainer.get(containerElement);
  if (!root) {
    root = createRoot(containerElement);
    rootsByContainer.set(containerElement, root);
  }

  root.render(wrappedComponent);
}

/**
 * Unmounts a tree rendered by createReactRoot into container.
 *
 * ReactDOM.unmountComponentAtNode does nothing to a createRoot container.
 */
export function unmountReactRoot(container: Element): void {
  const root = rootsByContainer.get(container);
  if (!root) {
    ReactDOM.unmountComponentAtNode(container);
    return;
  }
  root.unmount();
  rootsByContainer.delete(container);
}
